import { useState, useEffect } from 'react';
import StatCard from '../../components/common/StatCard';
import Button from '../../components/common/Button';
import Icon from '../../components/common/Icon';
import { lazyCampaignsApi } from '../../services/api';
import './LazyMarketing.css';

const Campaigns = () => {
  const [loading, setLoading] = useState(true);
  const [campaigns, setCampaigns] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaignStats, setCampaignStats] = useState(null);
  const [newCampaign, setNewCampaign] = useState({
    name: '',
    subject: '',
    body: '',
    ai_enabled: false,
  });

  useEffect(() => {
    loadCampaigns();
  }, []);

  const loadCampaigns = async () => {
    try {
      setLoading(true);
      const data = await lazyCampaignsApi.list();
      setCampaigns(data.campaigns || []);
    } catch (err) {
      console.error('Failed to load campaigns:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadCampaignStats = async (campaignId) => {
    try {
      const stats = await lazyCampaignsApi.getStats(campaignId);
      setCampaignStats(stats);
      setSelectedCampaign(campaignId);
    } catch (err) {
      console.error('Failed to load campaign stats:', err);
    }
  };

  const handleCreateCampaign = async (e) => {
    e.preventDefault();
    try {
      await lazyCampaignsApi.create(newCampaign);
      setShowCreateModal(false);
      setNewCampaign({ name: '', subject: '', body: '', ai_enabled: false });
      loadCampaigns();
    } catch (err) {
      console.error('Failed to create campaign:', err);
    }
  };

  const handleStartCampaign = async (id) => {
    try {
      await lazyCampaignsApi.start(id);
      loadCampaigns();
    } catch (err) {
      console.error('Failed to start campaign:', err);
    }
  };

  const handlePauseCampaign = async (id) => {
    try {
      await lazyCampaignsApi.pause(id);
      loadCampaigns();
    } catch (err) {
      console.error('Failed to pause campaign:', err);
    }
  };

  const handleDeleteCampaign = async (id) => {
    if (!confirm('Are you sure you want to delete this campaign?')) return;
    try {
      await lazyCampaignsApi.delete(id);
      loadCampaigns();
      if (selectedCampaign === id) {
        setSelectedCampaign(null);
        setCampaignStats(null);
      }
    } catch (err) {
      console.error('Failed to delete campaign:', err);
    }
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case 'running':
        return { background: 'var(--success-soft)', color: 'var(--success)' };
      case 'paused':
        return { background: 'var(--warning-soft)', color: 'var(--warning)' };
      case 'completed':
        return { background: 'var(--primary-soft)', color: 'var(--primary)' };
      default:
        return { background: 'var(--bg-tertiary)', color: 'var(--text-tertiary)' };
    }
  };

  const activeCampaigns = campaigns.filter(c => c.status === 'running').length;
  const totalContacts = campaigns.reduce((sum, c) => sum + (c.contact_count || 0), 0);

  const stats = [
    { label: 'Total Campaigns', value: campaigns.length.toString(), change: '', changeType: 'neutral' },
    { label: 'Active', value: activeCampaigns.toString(), change: activeCampaigns > 0 ? 'Running' : 'None', changeType: activeCampaigns > 0 ? 'positive' : 'neutral' },
    { label: 'Draft', value: campaigns.filter(c => c.status === 'draft').length.toString(), change: '', changeType: 'neutral' },
    { label: 'Total Contacts', value: totalContacts.toString(), change: '', changeType: 'neutral' },
  ];

  return (
    <div className="lm-page">
      <div className="lm-page-header">
        <div>
          <h1 className="lm-page-title">Email Campaigns</h1>
          <p className="lm-page-subtitle">Create and manage cold email outreach campaigns</p>
        </div>
        <div className="lm-page-actions">
          <Button variant="secondary" icon="refresh-cw" onClick={loadCampaigns} disabled={loading}>
            Refresh
          </Button>
        </div>
      </div>

      <div className="lm-stats-row">
        {stats.map((stat, index) => (
          <StatCard key={index} {...stat} loading={loading} />
        ))}
      </div>

      <div className="lm-section">
        <div className="lm-section-header">
          <h3 className="lm-section-title">Campaigns</h3>
          <Button variant="primary" size="sm" icon="plus" onClick={() => setShowCreateModal(true)}>
            New Campaign
          </Button>
        </div>

        {loading ? (
          <div className="lm-loading">Loading campaigns...</div>
        ) : campaigns.length === 0 ? (
          <div className="lm-empty">
            <Icon name="mail" size="xl" />
            <p>No campaigns yet. Create your first email campaign.</p>
            <Button variant="primary" icon="plus" onClick={() => setShowCreateModal(true)}>
              Create Campaign
            </Button>
          </div>
        ) : (
          <div className="lm-table">
            <div className="lm-table-header">
              <span>Campaign</span>
              <span>Status</span>
              <span>Contacts</span>
              <span>Sent</span>
              <span>Opens</span>
              <span>Created</span>
              <span>Actions</span>
            </div>
            {campaigns.map((campaign) => (
              <div
                key={campaign.id}
                className={`lm-table-row ${selectedCampaign === campaign.id ? 'selected' : ''}`}
                onClick={() => loadCampaignStats(campaign.id)}
              >
                <div className="lm-table-cell">
                  <span className="lm-table-primary">{campaign.name}</span>
                  {campaign.subject && (
                    <span className="lm-table-secondary">{campaign.subject}</span>
                  )}
                </div>
                <div className="lm-table-cell">
                  <span className="lm-grid-card-badge" style={getStatusStyle(campaign.status)}>
                    {campaign.status || 'draft'}
                  </span>
                </div>
                <div className="lm-table-cell">{campaign.contact_count || 0}</div>
                <div className="lm-table-cell">{campaign.sent_count || 0}</div>
                <div className="lm-table-cell">{campaign.open_count || 0}</div>
                <div className="lm-table-cell">
                  {campaign.created_at ? new Date(campaign.created_at).toLocaleDateString() : '--'}
                </div>
                <div className="lm-table-cell lm-table-actions">
                  {campaign.status === 'running' ? (
                    <button className="lm-action-btn" title="Pause" onClick={(e) => { e.stopPropagation(); handlePauseCampaign(campaign.id); }}>
                      <Icon name="pause" />
                    </button>
                  ) : (
                    <button className="lm-action-btn success" title="Start" onClick={(e) => { e.stopPropagation(); handleStartCampaign(campaign.id); }}>
                      <Icon name="play" />
                    </button>
                  )}
                  <button className="lm-action-btn danger" title="Delete" onClick={(e) => { e.stopPropagation(); handleDeleteCampaign(campaign.id); }}>
                    <Icon name="trash-2" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Campaign Stats Panel */}
      {campaignStats && (
        <div className="lm-section">
          <div className="lm-section-header">
            <h3 className="lm-section-title">Campaign Stats: {campaignStats.name}</h3>
            <button className="lm-close-btn" onClick={() => { setSelectedCampaign(null); setCampaignStats(null); }}>
              <Icon name="x" />
            </button>
          </div>
          <div className="lm-stats-grid">
            <div className="lm-stat-box">
              <span className="lm-stat-label">Total Contacts</span>
              <span className="lm-stat-value">{campaignStats.totalContacts}</span>
            </div>
            <div className="lm-stat-box">
              <span className="lm-stat-label">Sent</span>
              <span className="lm-stat-value">{campaignStats.sent}</span>
            </div>
            <div className="lm-stat-box">
              <span className="lm-stat-label">Open Rate</span>
              <span className="lm-stat-value">{campaignStats.openRate}%</span>
            </div>
            <div className="lm-stat-box">
              <span className="lm-stat-label">Click Rate</span>
              <span className="lm-stat-value">{campaignStats.clickRate}%</span>
            </div>
            <div className="lm-stat-box">
              <span className="lm-stat-label">Reply Rate</span>
              <span className="lm-stat-value">{campaignStats.replyRate}%</span>
            </div>
            <div className="lm-stat-box">
              <span className="lm-stat-label">Bounce Rate</span>
              <span className="lm-stat-value">{campaignStats.bounceRate}%</span>
            </div>
          </div>
        </div>
      )}

      {/* Create Campaign Modal */}
      {showCreateModal && (
        <div className="lm-modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="lm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="lm-modal-header">
              <h3>Create New Campaign</h3>
              <button className="lm-close-btn" onClick={() => setShowCreateModal(false)}>
                <Icon name="x" />
              </button>
            </div>
            <form onSubmit={handleCreateCampaign}>
              <div className="lm-form-group">
                <label>Campaign Name *</label>
                <input
                  type="text"
                  value={newCampaign.name}
                  onChange={(e) => setNewCampaign({ ...newCampaign, name: e.target.value })}
                  placeholder="e.g., Q1 Outreach"
                  required
                />
              </div>
              <div className="lm-form-group">
                <label>Email Subject</label>
                <input
                  type="text"
                  value={newCampaign.subject}
                  onChange={(e) => setNewCampaign({ ...newCampaign, subject: e.target.value })}
                  placeholder="e.g., Quick question about {{company}}"
                />
              </div>
              <div className="lm-form-group">
                <label>Email Body</label>
                <textarea
                  value={newCampaign.body}
                  onChange={(e) => setNewCampaign({ ...newCampaign, body: e.target.value })}
                  placeholder="Hi {{first_name}},&#10;&#10;I noticed..."
                  rows={6}
                />
              </div>
              <div className="lm-form-group lm-checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={newCampaign.ai_enabled}
                    onChange={(e) => setNewCampaign({ ...newCampaign, ai_enabled: e.target.checked })}
                  />
                  Enable AI auto-responses
                </label>
              </div>
              <div className="lm-modal-actions">
                <Button variant="secondary" onClick={() => setShowCreateModal(false)}>Cancel</Button>
                <Button variant="primary" type="submit">Create Campaign</Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Campaigns;
